/**
 * Help Center Page
 *
 * FAQ and customer support information for PetMas customers.
 * Organized by category with expandable sections.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: React.ReactNode;
  items: FAQItem[];
}

const FAQ_CATEGORIES: FAQCategory[] = [
  {
    title: 'Pedidos y Envios',
    icon: <TruckIcon />,
    items: [
      {
        question: '¿Cuanto tiempo demora mi pedido en llegar?',
        answer:
          'Los pedidos dentro de Lima Metropolitana se entregan en 24-48 horas habiles. Para provincias, el tiempo de entrega es de 3-5 dias habiles dependiendo de la ubicacion. Recibiras un codigo de seguimiento por email una vez despachado tu pedido.',
      },
      {
        question: '¿Cual es el costo del envio?',
        answer:
          'El envio es GRATIS para pedidos mayores a S/100 en Lima Metropolitana. Para pedidos menores, el costo es de S/10. Para envios a provincias, el costo varia segun la ubicacion y peso del pedido.',
      },
      {
        question: '¿Puedo rastrear mi pedido?',
        answer:
          'Si, una vez que tu pedido sea despachado, recibiras un email con el numero de seguimiento. Tambien puedes ver el estado de tu pedido iniciando sesion en tu cuenta en la seccion "Mis Pedidos".',
      },
      {
        question: '¿Hacen entregas los fines de semana?',
        answer:
          'Realizamos entregas de lunes a sabado. Los pedidos realizados despues de las 2pm se procesan al dia siguiente habil.',
      },
    ],
  },
  {
    title: 'Pagos',
    icon: <CreditCardIcon />,
    items: [
      {
        question: '¿Que metodos de pago aceptan?',
        answer:
          'Aceptamos tarjetas de credito y debito (Visa, Mastercard, American Express), transferencias bancarias, Yape, Plin, y pago contra entrega en Lima (solo efectivo).',
      },
      {
        question: '¿Es seguro comprar en PetMas?',
        answer:
          'Absolutamente. Utilizamos encriptacion SSL de 256 bits para proteger tus datos. No almacenamos informacion de tarjetas de credito en nuestros servidores - todo es procesado por pasarelas de pago certificadas.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer:
          'Si, puedes pagar en hasta 12 cuotas sin intereses con tarjetas de credito participantes. Las cuotas disponibles se muestran al momento del pago.',
      },
      {
        question: '¿Emiten boleta o factura?',
        answer:
          'Si, emitimos comprobantes electronicos. Puedes elegir entre boleta o factura durante el checkout. Para factura, necesitaras ingresar tu RUC y razon social.',
      },
    ],
  },
  {
    title: 'Productos',
    icon: <PackageIcon />,
    items: [
      {
        question: '¿Los productos son originales?',
        answer:
          'Todos nuestros productos son 100% originales. Trabajamos directamente con distribuidores autorizados y marcas reconocidas. Cada producto cuenta con su registro sanitario correspondiente.',
      },
      {
        question: '¿Como se que alimento es el adecuado para mi mascota?',
        answer:
          'Puedes usar nuestro chat con IA que te ayudara a encontrar el alimento ideal segun la edad, raza, tamaño y necesidades especificas de tu mascota. Tambien puedes filtrar por etapa de vida en cada categoria.',
      },
      {
        question: '¿Venden alimentos medicados?',
        answer:
          'Si, contamos con lineas de alimentos medicados para condiciones especificas como problemas renales, digestivos, alergias, etc. Recomendamos consultar con tu veterinario antes de cambiar a una dieta medicada.',
      },
      {
        question: '¿Los precios incluyen IGV?',
        answer:
          'Si, todos los precios mostrados en la web incluyen IGV.',
      },
    ],
  },
  {
    title: 'Cuenta y Registro',
    icon: <UserIcon />,
    items: [
      {
        question: '¿Es necesario crear una cuenta para comprar?',
        answer:
          'No es obligatorio, puedes comprar como invitado. Sin embargo, crear una cuenta te permite ver tu historial de pedidos, guardar direcciones, acumular puntos y recibir ofertas exclusivas.',
      },
      {
        question: '¿Como recupero mi contrasena?',
        answer:
          'En la pagina de inicio de sesion, haz clic en "¿Olvidaste tu contrasena?". Ingresa tu email y te enviaremos un enlace para crear una nueva contrasena.',
      },
      {
        question: '¿Puedo cambiar mi email de cuenta?',
        answer:
          'Si, puedes actualizar tu email en la seccion "Mi Cuenta" > "Datos Personales". Necesitaras verificar el nuevo email antes de que el cambio sea efectivo.',
      },
    ],
  },
  {
    title: 'Devoluciones y Garantias',
    icon: <RefreshIcon />,
    items: [
      {
        question: '¿Puedo devolver un producto?',
        answer:
          'Si, aceptamos devoluciones dentro de los 7 dias posteriores a la recepcion, siempre que el producto este sin abrir y en su empaque original. Para alimentos, no se aceptan devoluciones una vez abiertos por razones de higiene.',
      },
      {
        question: '¿Que hago si mi producto llega danado?',
        answer:
          'Contactanos inmediatamente por WhatsApp o email con fotos del producto danado. Te enviaremos un reemplazo sin costo adicional o te reembolsaremos el monto total.',
      },
      {
        question: '¿Tienen garantia los accesorios?',
        answer:
          'Si, todos los accesorios tienen garantia de 30 dias contra defectos de fabricacion. Las camas, transportadoras y juguetes estan cubiertos.',
      },
    ],
  },
];

export function HelpCenterPage() {
  const [openCategory, setOpenCategory] = useState<number | null>(0);
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  const toggleCategory = (index: number) => {
    setOpenCategory(openCategory === index ? null : index);
    setOpenQuestion(null);
  };

  const toggleQuestion = (key: string) => {
    setOpenQuestion(openQuestion === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Centro de Ayuda
          </h1>
          <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
            Encuentra respuestas a las preguntas mas frecuentes sobre pedidos,
            envios, pagos y mas.
          </p>
        </div>
      </div>

      {/* Quick Contact */}
      <div className="container mx-auto px-4 -mt-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 grid md:grid-cols-3 gap-6">
          <a
            href="https://wa.me/51999999999"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 rounded-xl bg-green-50 hover:bg-green-100 transition-colors group"
          >
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white group-hover:scale-110 transition-transform">
              <WhatsAppIcon />
            </div>
            <div>
              <p className="font-semibold text-gray-900">WhatsApp</p>
              <p className="text-sm text-gray-600">Respuesta inmediata</p>
            </div>
          </a>

          <a
            href="mailto:ayuda@petmas.pe"
            className="flex items-center gap-4 p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors group"
          >
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white group-hover:scale-110 transition-transform">
              <EmailIcon />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Email</p>
              <p className="text-sm text-gray-600">ayuda@petmas.pe</p>
            </div>
          </a>

          <a
            href="tel:+5114445555"
            className="flex items-center gap-4 p-4 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors group"
          >
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white group-hover:scale-110 transition-transform">
              <PhoneIcon />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Telefono</p>
              <p className="text-sm text-gray-600">(01) 444-5555</p>
            </div>
          </a>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
          Preguntas Frecuentes
        </h2>

        <div className="max-w-4xl mx-auto space-y-4">
          {FAQ_CATEGORIES.map((category, categoryIndex) => (
            <div
              key={category.title}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(categoryIndex)}
                className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                    {category.icon}
                  </div>
                  <span className="font-semibold text-gray-900">
                    {category.title}
                  </span>
                </div>
                <ChevronIcon
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    openCategory === categoryIndex ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Questions */}
              {openCategory === categoryIndex && (
                <div className="border-t border-gray-100">
                  {category.items.map((item, itemIndex) => {
                    const questionKey = `${categoryIndex}-${itemIndex}`;
                    return (
                      <div
                        key={questionKey}
                        className="border-b border-gray-50 last:border-b-0"
                      >
                        <button
                          onClick={() => toggleQuestion(questionKey)}
                          className="w-full flex items-center justify-between p-4 pl-16 hover:bg-gray-50 transition-colors text-left"
                        >
                          <span className="text-gray-700 pr-4">
                            {item.question}
                          </span>
                          <ChevronIcon
                            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                              openQuestion === questionKey ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {openQuestion === questionKey && (
                          <div className="px-16 pb-4 text-gray-600 text-sm leading-relaxed animate-fadeIn">
                            {item.answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            ¿No encontraste lo que buscabas?
          </h3>
          <p className="text-gray-600 mb-6">
            Nuestro equipo esta listo para ayudarte con cualquier consulta.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/51999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors"
            >
              <WhatsAppIcon />
              Contactar por WhatsApp
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Volver a la tienda
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function TruckIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

function PackageIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}
